// Printable report packet (HTML-first, so the PDF worker renders the same pages). Layout follows NGF's approved
// design (2026-09-27): letterhead, blue title band, participant block, one Letter page per section, boxed summary
// and a "Page n of N" footer. Pages: summary (always), coronary risk, spirometry, body composition, aerobic — each
// only when recorded. Imported ePhysioLogic results may lack any value; every number shows "—" when absent.
import type { AssessmentResult } from '@ngf/calc-engine';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { smokingStatus } from '../components/Intake.tsx';
import { ErrorBox, IS_DEV } from '../components/Layout.tsx';
import { useData } from '../lib/useData.ts';
import type { Measurements } from './Assessment.tsx';
import type { Review } from './Review.tsx';

interface Doc {
  id: string; assessedOn: string; status: string; ageYears: number | null; firstName: string; lastName: string; visitType?: 'physical' | 'cohsg' | 'labs';
  sex: 'M' | 'F' | null; employeeId: string | null; departmentName: string | null; measurements: Measurements & {
    activities?: Record<string, 'B' | 'I' | 'A'> | null; intensityPct?: number | null;
  };
  results: AssessmentResult | null; engineVersion: string | null; review: Review | null; supersededBy?: string | null;
  previous: { assessedOn: string; measurements: Measurements; results: AssessmentResult | null } | null;
}

// Reports made on dev (in the browser there, or by the dev PDF worker, which passes env=dev) carry a watermark.
export const DEV_REPORT = IS_DEV || (typeof location !== 'undefined' && new URLSearchParams(location.search).get('env') === 'dev');
export const CLINIC = 'North Greenville Fitness and Cardiac Rehabilitation Clinic, Inc.';
export const PHONE = '888-348-8911';

/** Fixed decimals, or a dash. */
const fx = (x: number | null | undefined, d: number, unit = '') => (x === null || x === undefined || !Number.isFinite(x) ? '—' : `${x.toFixed(d)}${unit}`);
/** 2026-08-14 -> 8/14/2026 */
export const us = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${m}/${d}/${y}`;
};
const addDays = (iso: string, days: number) => {
  const t = new Date(`${iso}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
};

// ------------------------------------------------------------------ page frame
export function Letterhead({ reportDate }: { reportDate: string }) {
  return (
    <header className="rpt-head">
      <div className="rpt-brand">
        <img src="/ngf-logo.png" alt="" />
        <div><div className="rpt-name">North Greenville Fitness</div><div className="rpt-sub">and Cardiac Rehabilitation Clinic, Inc.</div></div>
      </div>
      <div className="rpt-addr">907 N. Main St., P.O. Box 606, Travelers Rest, SC 29690<br />{PHONE} · northgreenvillefitness.com<br />Report date: {reportDate}</div>
    </header>
  );
}

function Person({ d, right }: { d: Doc; right: ReactNode }) {
  const m = d.measurements;
  const smoke = smokingStatus(m.smoking);
  const lifestyle = [smoke === 'current' ? 'Smoker' : smoke === 'former' ? 'Former Smoker' : smoke === 'never' ? 'Non-Smoker' : null,
    m.regularExercise === true ? 'Regular Physical Activity' : m.regularExercise === false ? 'No Regular Physical Activity' : null].filter(Boolean).join(', ');
  return (
    <div className="rpt-person">
      <div>
        <div className="rpt-pname">{d.firstName} {d.lastName}</div>
        <div>{d.sex === 'M' ? 'Male' : d.sex === 'F' ? 'Female' : ''}{d.ageYears ? `, age ${d.ageYears.toFixed(2)} years` : ''}</div>
        {lifestyle && <div>{lifestyle}</div>}
        {d.employeeId && <div>ID # {d.employeeId}</div>}
      </div>
      <div className="rpt-right">{right}</div>
    </div>
  );
}

/** Height and weight in the page header; "Refused" when the person refused (Tammy, 2026-10-28). */
function HeightWeight({ m }: { m: Doc['measurements'] }) {
  return <>{m.heightIn ? <div>Height: {fx(m.heightIn, 2)} inches</div> : m.refused?.height ? <div>Height: Refused</div> : null}
    {m.weightLb ? <div>Weight: {fx(m.weightLb, 2)} lbs</div> : m.refused?.weight ? <div>Weight: Refused</div> : null}</>;
}

/** Why there is no coronary risk score (option B, NGF 2026-10-28: no estimate; the page prints and says why). */
function riskNotCalculated(m: Doc['measurements']): string {
  if (m.refused?.bp) return 'Not calculated: blood pressure refused';
  const miss = [!m.restSbp || !m.restDbp ? 'blood pressure' : '', !m.totalChol ? 'total cholesterol' : '', !m.hdl ? 'HDL' : ''].filter(Boolean);
  return miss.length ? `Not calculated: no ${miss.length === 1 ? miss[0] : `${miss.slice(0, -1).join(', ')} or ${miss[miss.length - 1]}`}` : 'Not calculated';
}

function Page({ id, title, d, n, total, reportDate, right, children, summary }: {
  id: string; title: string; d: Doc; n: number; total: number; reportDate: string; right?: ReactNode; children: ReactNode; summary?: ReactNode;
}) {
  const m = d.measurements;
  return (
    <section className="rpt-page" data-page={id}>
      {DEV_REPORT && <div className="rpt-dev">DEV · TEST DATA · NOT A MEDICAL RECORD</div>}
      <Letterhead reportDate={reportDate} />
      <h1 className="rpt-title">{title}</h1>
      <Person d={d} right={right ?? <><HeightWeight m={m} /><div>Assessed: {us(d.assessedOn)}</div></>} />
      <div className="rpt-body">{children}</div>
      {summary && <div className="rpt-summary"><div className="rpt-summary-tag">Summary</div>{summary}</div>}
      <footer className="rpt-foot"><span>{CLINIC} · {PHONE}</span><span>{d.firstName} {d.lastName} · Assessed {us(d.assessedOn)} · Page {n} of {total}</span></footer>
    </section>
  );
}

// ------------------------------------------------------------------ coronary risk
type Level = 'ok' | 'warn' | 'bad' | '';
interface RiskRow { label: string; note?: string; cur: number | null | undefined; prev: number | null | undefined; dec: number; desirable: string; high: string; level: (v: number) => Level }
const band = (v: number, good: (v: number) => boolean, bad: (v: number) => boolean): Level => (bad(v) ? 'bad' : good(v) ? 'ok' : 'warn');
const CLASSES = ['Poor', 'Fair', 'Average', 'Good', 'Excellent'] as const;

function RiskBar({ cls }: { cls: string | null | undefined }) {
  const k = CLASSES.indexOf((cls ?? '') as (typeof CLASSES)[number]);
  return <div className="rpt-riskbar">{CLASSES.map((c, i) => <span key={c} className={i <= k ? 'on' : ''} />)}</div>;
}

function CoronaryPage(props: { d: Doc; n: number; total: number; reportDate: string }) {
  const { d } = props;
  const r = d.results!.coronaryRisk ?? null, p = d.previous?.results?.coronaryRisk ?? null;
  const m = d.measurements, pm = d.previous?.measurements ?? {};
  const ratio = d.results!.cholHdlRatio ?? (m.totalChol && m.hdl ? m.totalChol / m.hdl : null);
  const pratio = d.previous?.results?.cholHdlRatio ?? (pm.totalChol && pm.hdl ? pm.totalChol / pm.hdl : null);
  const smoker = smokingStatus(m.smoking);
  const bpNote = m.refused?.bp ? 'Refused' : undefined;
  const groups: [string, RiskRow[]][] = [
    ['Blood Pressure', [
      { label: 'Systolic Blood Pressure:', note: bpNote, cur: m.restSbp, prev: pm.restSbp, dec: 0, desirable: '< 120 mm Hg', high: '>= 160 mm Hg', level: (v) => band(v, (x) => x < 120, (x) => x >= 160) },
      { label: 'Diastolic Blood Pressure:', note: bpNote, cur: m.restDbp, prev: pm.restDbp, dec: 0, desirable: '< 80 mm Hg', high: '>= 115 mm Hg', level: (v) => band(v, (x) => x < 80, (x) => x >= 115) },
    ]],
    ['Blood Lipid Profile', [
      { label: 'Total Cholesterol:', cur: m.totalChol, prev: pm.totalChol, dec: 0, desirable: '< 200 mg/dL', high: '>= 240 mg/dL', level: (v) => band(v, (x) => x < 200, (x) => x >= 240) },
      { label: 'HDL Cholesterol:', cur: m.hdl, prev: pm.hdl, dec: 0, desirable: '> 45 mg/dL', high: '< 35 mg/dL', level: (v) => band(v, (x) => x > 45, (x) => x < 35) },
      { label: 'Total / HDL Ratio:', cur: ratio, prev: pratio, dec: 2, desirable: '< 3.5 : 1', high: '> 5.5 : 1', level: (v) => band(v, (x) => x < 3.5, (x) => x > 5.5) },
      { label: 'LDL Cholesterol:', cur: m.ldl, prev: pm.ldl, dec: 0, desirable: '< 130 mg/dL', high: '>= 160 mg/dL', level: (v) => band(v, (x) => x < 130, (x) => x >= 160) },
      { label: 'Triglycerides:', cur: m.triglycerides, prev: pm.triglycerides, dec: 0, desirable: '< 200 mg/dL', high: '> 400 mg/dL', level: (v) => band(v, (x) => x < 200, (x) => x > 400) },
      { label: 'Blood Glucose:', cur: m.glucose, prev: pm.glucose, dec: 0, desirable: '< 115 mg/dL', high: '> 140 mg/dL', level: (v) => band(v, (x) => x < 115, (x) => x > 140) },
    ]],
    ['Smoking Habits', [
      { label: 'Average Cigarettes / Day:', cur: m.smoking?.cigsPerDay ?? 0, prev: pm.smoking?.cigsPerDay ?? (d.previous ? 0 : null), dec: 0, desirable: 'None', high: '> 1',
        level: (v) => (smoker === 'current' ? band(v, (x) => x === 0, (x) => x > 1) : '') },
      { label: 'Years Smoked:', cur: m.smoking?.yearsSmoked ?? 0, prev: pm.smoking?.yearsSmoked ?? (d.previous ? 0 : null), dec: 2, desirable: 'Never', high: '> 1 year',
        level: (v) => (smoker === 'current' ? band(v, (x) => x === 0, (x) => x > 1) : '') },
      { label: 'Years Since Quit:', cur: m.smoking?.yearsQuit ?? 0, prev: pm.smoking?.yearsQuit ?? (d.previous ? 0 : null), dec: 2, desirable: '>= 10 yrs', high: '< 10 yrs',
        level: (v) => (smoker === 'former' ? (v >= 10 ? 'ok' : 'bad') : '') },
    ]],
  ];
  const high: string[] = [], above: string[] = [];
  for (const [, rows] of groups) for (const row of rows) {
    if (row.cur === null || row.cur === undefined) continue;
    const l = row.level(row.cur);
    const name = row.label.replace(/:$/, '').replace('Average ', '');
    if (l === 'bad') high.push(name); else if (l === 'warn') above.push(name);
  }
  const list = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
  return (
    <Page id="coronary_risk" title="Coronary Risk Report" {...props}
      summary={<p>To improve or maintain your current level of coronary risk, compare your results to the desirable levels shown above.{' '}
        {high.length ? <>Your <strong>{list(high)}</strong> {high.length === 1 ? 'is' : 'are'} in the high-risk range; consult your physician. </> : 'None of your results fall in the high-risk category. '}
        {above.length ? <>The following are above the desirable level and are worth attention: {list(above)}. </> : ''}
        Remember, all of these risk factors are modifiable. Use the resources available to help you with your fitness goals, and consult your exercise
        specialist and dietitian to help design the wellness program best suited to your needs.</p>}>
      <p className="rpt-intro">Cardiovascular disease remains one of the leading causes of death in the United States. To help reduce cardiovascular heart disease (CVD),
        health organizations such as the American College of Sports Medicine (ACSM) have designed guidelines based on risk. Many factors contribute to coronary risk;
        among the most important are hypertension, hyperlipidemia and cigarette smoking, all of which are modifiable. The table below shows your primary risk factors as reported.</p>
      <table className="rpt-table rpt-risk">
        <thead><tr><th>Modifiable Risk Factors</th><th>Current</th><th>Previous</th><th>Desirable</th><th>High Risk</th></tr></thead>
        <tbody>{groups.map(([g, rows], gi) => [
          <tr key={g} className="rpt-group"><td>{g}</td><td>{gi === 0 ? us(d.assessedOn) : ''}</td><td>{gi === 0 && d.previous ? us(d.previous.assessedOn) : ''}</td><td /><td /></tr>,
          ...rows.map((row) => (
            <tr key={row.label}><td className="rpt-ind">{row.label}</td>
              <td className={row.cur !== null && row.cur !== undefined ? `lvl-${row.level(row.cur)}` : ''}>{row.note && (row.cur === null || row.cur === undefined) ? row.note : fx(row.cur, row.dec)}</td>
              <td>{d.previous ? fx(row.prev, row.dec) : ''}</td><td>{row.desirable}</td><td>{row.high}</td></tr>)),
        ])}</tbody>
      </table>
      <h2 className="rpt-h">Overall Risk</h2>
      <div className="rpt-overall">
        <div /><div /><div className="rpt-classes">{CLASSES.map((c) => <span key={c}>{c}</span>)}</div><div />
        {p && <><div>Previous:</div><strong>{fx(p.score, 2)}</strong><RiskBar cls={p.riskClass} /><div>{p.riskClass}</div></>}
        {r ? <><div>Current:</div><strong>{fx(r.score, 2)}</strong><RiskBar cls={r.riskClass} /><div>{r.riskClass}</div></>
          : <><div>Current:</div><strong>—</strong><div className="rpt-notcalc">{riskNotCalculated(m)}</div><div /></>}
      </div>
      <p className="rpt-legend">Higher scores are better. <span className="lvl-bad">Red</span> = high-risk range · <span className="lvl-warn">amber</span> = above the desirable level ·{' '}
        <span className="lvl-ok">green</span> = desirable.</p>
    </Page>
  );
}

// ------------------------------------------------------------------ spirometry
function SpirometryPage(props: { d: Doc; n: number; total: number; reportDate: string }) {
  const { d } = props;
  const s = d.results!.spirometry!, ps = d.previous?.results?.spirometry ?? null;
  const m = d.measurements, pm = d.previous?.measurements ?? {};
  const block = (title: string, text: string, prev: ReactNode, cur: ReactNode, pred: ReactNode, pct?: number | null) => (
    <>
      <h2 className="rpt-h">{title}</h2>
      <p>{text}</p>
      <div className="rpt-ppc">{d.previous && <><b>Previous :</b><span>{prev}</span></>}<b>Current :</b><strong>{cur}</strong><b>Predicted :</b><span>{pred}</span></div>
      {pct !== undefined && <p>The CURRENT value is <strong>{fx(pct, 2, ' %')}</strong> of what is predicted for someone of your age, sex and size.</p>}
    </>
  );
  const low = [s.fvcPctPredicted, s.fev1PctPredicted].some((x) => typeof x === 'number' && x < 80);
  return (
    <Page id="spirometry" title="Pulmonary Function — Spirometry Report" {...props}
      summary={<ol>
        <li>Based upon your FEV1 %, your overall level of obstruction is <strong>{s.obstruction ?? '—'}</strong>.{ps?.obstruction ? ` Your previous level of obstruction was ${ps.obstruction}.` : ''}</li>
        <li>Your FVC is {fx(s.fvcPctPredicted, 1, ' %')} of predicted and your FEV1 is {fx(s.fev1PctPredicted, 1, ' %')} of predicted.
          {low ? ' Values below 80 % of predicted may warrant follow-up with your physician.' : ''}</li>
      </ol>}>
      <p className="rpt-intro">Your lung performance and capacity have been evaluated with a flow meter called a spirometer. Any deficiencies in the values below could suggest
        possible impairment due to smoking, allergies, infections or other medical conditions. Predicted values are based on your sex, age and height.</p>
      {block('Forced Vital Capacity (FVC)', 'The total volume of air that you can breathe out from your deepest inspiration to your deepest expiration is called your forced vital capacity. It usually increases with body size, and can be predicted by a person\'s age, height and sex.',
        fx(pm.fvc, 2), fx(m.fvc, 2), fx(s.fvcPredicted, 2), s.fvcPctPredicted)}
      {block('Forced Expiratory Volume, 1 Second (FEV1)', 'This measurement reports the amount of air that you were able to expire from your lungs in the first second of the test.',
        fx(pm.fev1, 2), fx(m.fev1, 2), fx(s.fev1Predicted, 2), s.fev1PctPredicted)}
      {block('FEV1 / FVC (FEV1 %)', 'This ratio indicates what percent of your lungs\' total volume can be expired in the first second of the test.',
        fx(ps?.fev1FvcPct, 2), fx(s.fev1FvcPct, 2), fx(s.fev1FvcPctPredicted, 2))}
    </Page>
  );
}

// ------------------------------------------------------------------ body composition
interface Col { label: string; date: string; fat: number | null; lean: number | null; pct: number | null }

function StackChart({ cols }: { cols: Col[] }) {
  const W = 820, H = 300, base = 270, top = 40;
  const max = Math.max(...cols.map((c) => (c.fat ?? 0) + (c.lean ?? 0)), 1);
  const scale = (v: number) => ((base - top) * v) / max;
  const bw = 115, gap = (W - 120 - cols.length * bw) / cols.length;
  return (
    <svg viewBox={`0 0 ${W} ${H + 30}`} className="rpt-chart" role="img" aria-label="Total weight: fat and lean">
      <text x={130} y={16} className="rpt-ctitle">Total weight (lbs)</text>
      <line x1={110} y1={20} x2={110} y2={base} className="rpt-axis" /><line x1={110} y1={base} x2={W - 20} y2={base} className="rpt-axis" />
      {cols.map((c, i) => {
        const x = 110 + gap / 2 + i * (bw + gap), lean = scale(c.lean ?? 0), fat = scale(c.fat ?? 0), total = (c.fat ?? 0) + (c.lean ?? 0);
        return (<g key={c.label}>
          <rect x={x} y={base - lean} width={bw} height={lean} className="rpt-lean" />
          <rect x={x} y={base - lean - fat} width={bw} height={fat} className="rpt-fat" />
          <text x={x + bw / 2} y={base - lean - fat - 8} textAnchor="middle" className="rpt-val">{Number.isInteger(total) ? total : total.toFixed(2)}</text>
          <text x={x + bw / 2} y={base + 24} textAnchor="middle" className="rpt-clabel">{c.label}</text>
        </g>);
      })}
    </svg>
  );
}

function BodyPage(props: { d: Doc; n: number; total: number; reportDate: string }) {
  const { d } = props;
  const b = d.results!.bodyComposition!, pb = d.previous?.results?.bodyComposition ?? null;
  const m = d.measurements;
  const weight = m.weightLb ?? ((b.fatWeightLb ?? 0) + (b.leanWeightLb ?? 0));
  const cols: Col[] = [];
  if (pb && d.previous) cols.push({ label: 'Previous', date: d.previous.assessedOn, fat: pb.fatWeightLb, lean: pb.leanWeightLb, pct: pb.fatPct });
  cols.push({ label: 'Current', date: d.assessedOn, fat: b.fatWeightLb, lean: b.leanWeightLb, pct: b.fatPct });
  const g = b.goals;
  const lean = b.leanWeightLb ?? null;
  if (g) {
    cols.push({ label: 'Goal', date: addDays(d.assessedOn, g.shortTerm.days), fat: g.shortTerm.fatWeightLb, lean, pct: g.shortTerm.fatPct });
    cols.push({ label: 'Target', date: addDays(d.assessedOn, g.target.days), fat: g.target.fatWeightLb, lean, pct: g.target.fatPct });
  } else {
    // At or below the target % (NGF 2026-09-25: keep the weight at the target %, not the current weight).
    cols.push({ label: 'Goal', date: d.assessedOn, fat: b.fatWeightLb, lean: b.leanWeightLb, pct: b.fatPct });
    if (b.targetFatPct != null && b.targetWeightLb != null && lean != null) {
      cols.push({ label: 'Target', date: d.assessedOn, fat: b.targetWeightLb - lean, lean, pct: b.targetFatPct });
    }
  }
  const range = b.targetRange;
  const where = range && b.fatPct != null ? (b.fatPct < range[0] ? 'under' : b.fatPct > range[1] ? 'over' : 'within') : null;
  const diff = b.targetWeightLb != null ? weight - b.targetWeightLb : null;
  return (
    <Page id="body_composition" title="Body Composition Report" {...props}
      right={<><div>{us(d.assessedOn)}</div>{m.heightIn ? <div>Height: {fx(m.heightIn, 2)} inches</div> : null}<div>Weight: {fx(weight, 2)} lbs</div></>}
      summary={<ol>
        <li>Your current weight of {fx(weight, 2)} lbs consists of {fx(b.fatPct, 2, '%')} fat ({fx(b.fatWeightLb, 2)} lb) and {fx(b.leanWeightLb, 2)} lb of muscle,
          bone and connective tissue. Body fat was estimated from skinfold measurements.</li>
        {where && range && <li>Your body fat percentage, {fx(b.fatPct, 2, '%')}, is <strong>{where}</strong> the target body fat range for your age, {fx(range[0], 2)}–{fx(range[1], 2)}%.</li>}
        {diff != null && b.targetFatPct != null && <li>Your target body fat percentage, {fx(b.targetFatPct, 2, '%')}, means that you are{' '}
          <strong>{fx(Math.abs(diff), 2)} lb {diff >= 0 ? 'over' : 'under'}</strong> your target weight of {fx(b.targetWeightLb, 2)} lb.</li>}
      </ol>}>
      <p className="rpt-intro">From a health perspective, your body weight is not as important as how much of that weight consists of fat. Your body weight can be divided into
        fat weight and lean body weight, which is made up of the remaining components of the body. Your target weight may be reached by increasing lean muscle and
        decreasing body fat to an ideal level.</p>
      <div className="rpt-keys"><span><i className="rpt-fat" /> Fat weight</span><span><i className="rpt-lean" /> Lean weight</span></div>
      <StackChart cols={cols} />
      <table className="rpt-table rpt-cols">
        <thead><tr><th />{cols.map((c) => <th key={c.label}>{c.label}</th>)}</tr></thead>
        <tbody>
          <tr><td><b>Date</b></td>{cols.map((c) => <td key={c.label}>{us(c.date)}</td>)}</tr>
          <tr><td><b>Fat Weight</b> (lbs)</td>{cols.map((c) => <td key={c.label}>{fx(c.fat, 2)}</td>)}</tr>
          <tr><td><b>Lean Weight</b> (lbs)</td>{cols.map((c) => <td key={c.label}>{fx(c.lean, 2)}</td>)}</tr>
          <tr><td><b>% Body Fat</b></td>{cols.map((c) => <td key={c.label}>{fx(c.pct, 2)}</td>)}</tr>
        </tbody>
      </table>
      {b.targetWeightLb != null && <p className="rpt-intro">Within 5 pounds of your target weight ({fx(b.targetWeightLb - 5, 2)}–{fx(b.targetWeightLb + 5, 2)} lbs) there is essentially
        no difference in your risk of death due to heart disease. Lowering body weight and fat to levels well below the target may be desired for improved performance
        in endurance activities or for appearance, but from a health perspective it is not recommended. An extremely low level of body fat can be hazardous to your
        health, especially if it is due to an eating disorder.</p>}
    </Page>
  );
}

// ------------------------------------------------------------------ aerobic capacity
function AerobicChart({ prev, cur, ach }: { prev: number | null; cur: number; ach: number | null }) {
  const bars = [['Previous', prev, 'rpt-prev'], ['Current', cur, 'rpt-cur'], ['Achievable', ach, 'rpt-ach']] as const;
  const W = 820, base = 290, top = 60;
  const max = Math.max(...bars.map(([, v]) => v ?? 0), 1);
  const y = (v: number) => base - ((base - top) * v) / max;
  return (
    <svg viewBox={`0 0 ${W} 330`} className="rpt-chart" role="img" aria-label="Aerobic capacity">
      <text x={130} y={24} className="rpt-ctitle">Aerobic capacity (ml/kg/min)</text>
      <line x1={120} y1={36} x2={120} y2={base} className="rpt-axis" /><line x1={120} y1={base} x2={560} y2={base} className="rpt-axis" />
      {bars.map(([label, v, cls], i) => {
        const x = 150 + i * 145;
        return (<g key={label}>
          {v != null && <><rect x={x} y={y(v)} width={82} height={base - y(v)} className={cls} />
            <line x1={112} y1={y(v)} x2={120} y2={y(v)} className="rpt-axis" /><text x={106} y={y(v) + 5} textAnchor="end" className="rpt-tick">{v}</text></>}
          <text x={x + 41} y={base + 26} textAnchor="middle" className="rpt-clabel">{label}</text>
        </g>);
      })}
      <text x={600} y={90} className="rpt-life">Changing Lifestyle:</text><text x={600} y={116} className="rpt-life2">Exercise</text>
    </svg>
  );
}

const ACTIVITY_LABEL: Record<string, string> = {
  walking: 'Walking', treadmillWalking: 'Treadmill Walking', jogging: 'Jogging', treadmillJogging: 'Treadmill Jogging', cyclingAirdyne: 'Cycling (Airdyne)',
  cyclingLifecycle: 'Cycling (Lifecycle)', cyclingErgometer: 'Cycling (Ergometer)', swimmingFront: 'Swimming (Front)', swimmingBackstroke: 'Swimming (Backstroke)',
  swimmingSide: 'Swimming (Side)', swimmingBreaststroke: 'Swimming (Breaststroke)', elliptical: 'Elliptical',
};
const LEVEL: Record<string, string> = { B: 'Beginner', I: 'Intermediate', A: 'Advanced' };

function AerobicPage(props: { d: Doc; n: number; total: number; reportDate: string }) {
  const { d } = props;
  const a = d.results!.aerobic!, pa = d.previous?.results?.aerobic ?? null;
  const m = d.measurements;
  const vsAvg = a.pctOfAverage != null ? a.pctOfAverage - 100 : null;
  const acts = Object.entries(m.activities ?? {}).filter(([k]) => ACTIVITY_LABEL[k]).map(([k, v]) => `${ACTIVITY_LABEL[k]} (${LEVEL[v] ?? v})`);
  const tile = (big: ReactNode, small: string) => <div className="rpt-tile"><div>{big}</div><span>{small}</span></div>;
  return (
    <Page id="aerobic" title="Aerobic Capacity Report" {...props}
      right={<><div>{us(d.assessedOn)}, Workload: ACSM Cycle Ergometer</div><HeightWeight m={m} /></>}
      summary={<ol>
        <li>Your aerobic capacity is <strong>{fx(a.vo2maxDisplayed, 2)} ml/kg/min</strong>
          {vsAvg != null && <> and is <strong>{fx(Math.abs(vsAvg), 2)}% {vsAvg >= 0 ? 'above' : 'below'} average</strong> for your age and sex</>}.</li>
        <li>Exercise Prescription:
          <div className="rpt-tiles">
            {tile('Aerobic', 'Type')}{tile('20–30 min', 'Duration')}{tile('3–5 ×/week', 'Frequency')}
            {tile(`${fx(a.mets, 2)} METs`, 'Intensity (70%)')}
            {tile(a.targetHr ? <>{a.targetHr.thr} <small>({a.targetHr.low}–{a.targetHr.high})</small></> : '—', 'Heart rate (bpm)')}
          </div>
          <div className="rpt-note">Target heart rate uses the heart-rate-reserve (Karvonen) method with your resting heart rate and your age-predicted maximum heart rate.</div>
        </li>
        {acts.length > 0 && <li>Recommended activities: {acts.join(', ')}.</li>}
      </ol>}>
      <p className="rpt-intro">Your aerobic capacity represents the maximal rate at which your body can supply energy for heavy exercise by burning oxygen. Because this oxygen
        is delivered to your muscles by your circulatory system, aerobic capacity is the best physiological measure of cardiovascular fitness.</p>
      <p className="rpt-intro">Aerobic capacity decreases with age, cardiovascular disease, smoking, weight gain and a sedentary lifestyle. However, it can be increased by regular
        exercise, weight loss and quitting smoking. The projected level of aerobic capacity you could reach with the lifestyle change shown is displayed below.</p>
      <AerobicChart prev={pa?.vo2maxDisplayed ?? null} cur={a.vo2maxDisplayed} ach={a.achievableVo2 ?? null} />
      {m.stages?.length ? (
        <table className="rpt-table rpt-stages">
          <thead><tr><th>Test stage</th><th>Workload</th><th>RPM</th><th>Heart rate</th></tr></thead>
          <tbody>
            {m.stages.map((s, i) => <tr key={i}><td>Stage {2 * (i + 1)}</td><td>{fx(s.kp, 1, ' KP')}</td><td>{s.rpm || '—'}</td><td>{s.hr ? `${s.hr} bpm` : '—'}</td></tr>)}
            <tr><td>Resting / Peak HR</td><td colSpan={2}>{m.peakSbp ? `Peak BP ${m.peakSbp}/${m.peakDbp ?? '—'}` : ''}</td><td>{m.restHr ?? '—'} / {m.peakHr ?? '—'} bpm</td></tr>
          </tbody>
        </table>) : null}
    </Page>
  );
}

// ------------------------------------------------------------------ summary (page 1)
function SummaryPage(props: { d: Doc; n: number; total: number; reportDate: string }) {
  const { d } = props;
  const r = d.results!, p = d.previous?.results ?? null;
  const m = d.measurements, pm = d.previous?.measurements ?? {};
  const rows: [string, ReactNode, ReactNode][] = [
    ['Aerobic capacity (ml/kg/min)', r.aerobic ? `${fx(r.aerobic.vo2maxDisplayed, 1)} · ${r.aerobic.aerobicClass ?? ''}` : '—', p?.aerobic ? `${fx(p.aerobic.vo2maxDisplayed, 1)} · ${p.aerobic.aerobicClass ?? ''}` : '—'],
    ['Body fat', r.bodyComposition ? fx(r.bodyComposition.fatPct, 1, '%') : '—', p?.bodyComposition ? fx(p.bodyComposition.fatPct, 1, '%') : '—'],
    ['Coronary risk score', r.coronaryRisk ? `${fx(r.coronaryRisk.score, 0)} · ${r.coronaryRisk.riskClass ?? ''}` : m.refused?.bp ? riskNotCalculated(m) : '—', p?.coronaryRisk ? `${fx(p.coronaryRisk.score, 0)} · ${p.coronaryRisk.riskClass ?? ''}` : '—'],
    ['Lung function', r.spirometry?.obstruction ?? '—', p?.spirometry?.obstruction ?? '—'],
    ['Blood pressure', m.restSbp ? `${m.restSbp}/${m.restDbp ?? '—'}` : m.refused?.bp ? 'Refused' : '—', pm.restSbp ? `${pm.restSbp}/${pm.restDbp ?? '—'}` : '—'],
    ['Resting heart rate', m.restHr ?? '—', pm.restHr ?? '—'],
    ['Total cholesterol / HDL', m.totalChol ? `${m.totalChol} / ${m.hdl ?? '—'}` : '—', pm.totalChol ? `${pm.totalChol} / ${pm.hdl ?? '—'}` : '—'],
    ['LDL / triglycerides', m.ldl ? `${m.ldl} / ${m.triglycerides ?? '—'}` : '—', pm.ldl ? `${pm.ldl} / ${pm.triglycerides ?? '—'}` : '—'],
    ['Glucose', m.glucose ?? '—', pm.glucose ?? '—'],
  ];
  const rv = d.review;
  return (
    <Page id="summary" title="Fitness Assessment Summary" {...props}
      summary={rv?.signatureKind === 'staff' ? (
        <div className="rpt-sign">
          <p>Reviewed by NGF staff; no physician signature required.</p>
          <div className="rpt-note">{rv.signerName} · {new Date(rv.signedAt).toLocaleDateString('en-US')} · record SHA-256 {rv.sha256.slice(0, 16)}…</div>
        </div>) : rv ? (
        <div className="rpt-sign">
          <p>Physician review: respirator {rv.respiratorQualified === null ? 'not assessed' : rv.respiratorQualified ? <strong>qualified</strong> : <strong>not qualified</strong>}
            {rv.referred ? <>; <strong>referred</strong>{rv.referralReason ? ` — ${rv.referralReason}` : ''}</> : '; not referred'}.</p>
          {rv.notes && <p>{rv.notes}</p>}
          {rv.signatureImage && <img src={rv.signatureImage} alt="Signature" />}
          <div>/s/ {rv.typedSignature}</div>
          <div className="rpt-note">{rv.signerName}{rv.signerCredential ? `, ${rv.signerCredential}` : ''} · signed {new Date(rv.signedAt).toLocaleDateString('en-US')}
            {' '}· record SHA-256 {rv.sha256.slice(0, 16)}…</div>
        </div>) : <p>Not yet reviewed by a physician.</p>}>
      <table className="rpt-table rpt-cols">
        <thead><tr><th>Result</th><th>Current ({us(d.assessedOn)})</th><th>{d.previous ? `Previous (${us(d.previous.assessedOn)})` : 'Previous'}</th></tr></thead>
        <tbody>{rows.map(([k, v, pv]) => <tr key={k}><td><b>{k}</b></td><td>{v}</td><td>{d.previous ? pv : '—'}</td></tr>)}</tbody>
      </table>
      {/* Staff comments (e.g. "Box messed up" copied from a scanned form) are internal: kept in the app, not printed (2026-09-30). */}
      {d.departmentName && <p className="rpt-intro">Organization: {d.departmentName}</p>}
    </Page>
  );
}

// ------------------------------------------------------------------ the packet
export function Report() {
  const { id } = useParams();
  const { data: d, error } = useData<Doc>(`/api/assessments/${id}`);
  // data-report-ready / data-report-error tell the PDF worker the page has finished loading.
  if (error) return <div data-report-error={error instanceof Error ? error.message : String(error)}><ErrorBox error={error} /></div>;
  if (!d) return <p className="muted">Loading…</p>;
  const r = d.results;
  const reportDate = us((d.review?.signedAt ?? new Date().toISOString()).slice(0, 10));
  // A COHSG screening prints the coronary risk page only; a labs-only visit has no report packet (the lab report is the result).
  const cohsg = d.visitType === 'cohsg';
  const pages = r && d.visitType !== 'labs' ? ([
    ['summary', cohsg ? null : SummaryPage], ['coronary', r.coronaryRisk || cohsg || d.measurements.refused?.bp ? CoronaryPage : null], ['spirometry', !cohsg && r.spirometry ? SpirometryPage : null],
    ['body', !cohsg && r.bodyComposition ? BodyPage : null], ['aerobic', !cohsg && r.aerobic ? AerobicPage : null],
  ] as const).filter(([, P]) => P) : [];
  return (
    <div data-report-ready="" className="rpt">
      <div className="no-print spread" style={{ marginBottom: '1rem' }}>
        <Link to={`/assessments/${d.id}`}>← Back to assessment</Link>
        <div className="actions">
          {d.status !== 'signed' && d.status !== 'released' && <span className="badge provisional">Draft — not yet signed by a physician</span>}
          <button onClick={() => window.print()}>Print / save as PDF</button>
        </div>
      </div>
      {d.supersededBy && <div className="alert error"><b>REPLACED BY A CORRECTION: this is an old report. Do not use or send it.</b>
        <span className="no-print"> <Link to={`/assessments/${d.supersededBy}/report`}>Open the corrected report →</Link></span></div>}
      {d.visitType === 'labs' ? <div className="alert info">Labs-only visit: there is no report packet. The results are in the lab report (on the person's page, Lab results and Documents).</div>
        : !r ? <div className="alert info">{d.status === 'signed' || d.status === 'released'
            ? <>No results were stored when this was signed: the person's sex or date of birth was missing then. Fix the person's record, then use
              {' '}<b>Create correction</b> on the assessment to issue a corrected report (its results are recalculated when it is finished).</>
            : 'No results yet — enter measurements (and the participant’s DOB and sex) first.'}</div>
        : pages.map(([key, P], i) => { const C = P!; return <C key={key} d={d} n={i + 1} total={pages.length} reportDate={reportDate} />; })}
      {r && <p className="muted small no-print">Calculated with engine {d.engineVersion}. Provisional rules: {r.provisional.join(', ') || 'none'}.</p>}
    </div>
  );
}
